/**
 * CORE-M-07 Keyword Review tab body.
 *
 * Chans's 2026-09-15 rebuild spec §2.7: keywords that spent without
 * converting; absorbs the current "Confirm Keyword" / "Confirm Ad" buckets.
 * Labelling must make the action unambiguous: it has to be obvious whether
 * Yes = pause this, add this, or keep this.
 *
 * What ships here now
 *
 *   The account's Search keywords, with spend, conversions, cost per
 *   conversion, CVR and the three quality buckets, plus bulk pause/enable
 *   where the account allows keyword writes. This table used to sit in the
 *   legacy Management body, where it rendered under ALL SEVEN category tabs
 *   — 779 rows on one account — which is most of why Art's 2026-09-22 review
 *   found the page "almost identical once you scroll past the top" whichever
 *   tab you were on (Gap A). It belongs here: this is the keyword tab.
 *
 * What is still to come
 *
 *   The FLAGGING. Art answered Q-3 with the thresholds (guards first: brand
 *   keyword, any conversion in 90 days, under 14 days of data, or waste that
 *   is really a bad search term; then fire on spend at 1.5-2x target CPA with
 *   no conversions, or clicks reaching 3 ÷ target CVR capped at 60). Until
 *   that lands, this tab shows every keyword ranked by spend and marks the
 *   wasteful ones, rather than claiming to have judged them.
 *
 *   ⚠ The previous shell told the operator this tab was "waiting on Art" and
 *   named him in the UI. It is no longer waiting on anybody, and an operator
 *   should not be reading a colleague's name in the product either.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  ManagementTable,
  type ManagementColumn,
} from '@/components/google-ads/ManagementTable';
import {
  canWrite,
  setKeywordStatus,
  type KeywordRow as GaKeywordRow,
} from '@/services/googleAdsManagementService';
import { useTenantStore } from '@/stores/useTenantStore';

import { ReadGate } from './ReadGate';
import {
  fmtConv,
  fmtMoney,
  qualityLabel,
  qualityRank,
} from './managementFormat';
import { useManagementStructure } from './useManagementReads';

interface KeywordReviewTabProps {
  tenantId?: string | null;
}

const keywordColumns: ManagementColumn<GaKeywordRow>[] = [
  { key: 'text', header: 'Keyword', width: 220, sort: (r) => r.text.toLowerCase(),
    render: (r) => r.text },
  { key: 'match_type', header: 'Match', width: 110, sort: (r) => r.match_type,
    render: (r) => <Badge variant="outline">{r.match_type || '—'}</Badge> },
  { key: 'ad_group', header: 'Ad group', width: 180,
    sort: (r) => r.ad_group_name.toLowerCase(), render: (r) => r.ad_group_name },
  { key: 'status', header: 'Status', width: 110, sort: (r) => r.status,
    render: (r) => (
      <Badge variant={r.status === 'ENABLED' ? 'secondary' : 'outline'}>
        {r.status || 'unknown'}
      </Badge>
    ) },
  { key: 'cost', header: 'Spend', width: 100, align: 'right', sort: (r) => r.cost,
    render: (r) => (
      <span className={r.is_waste ? 'font-medium text-destructive' : ''}>
        {fmtMoney(r.cost)}
      </span>
    ) },
  { key: 'conversions', header: 'Conv.', width: 90, align: 'right',
    sort: (r) => r.conversions, render: (r) => fmtConv(r.conversions) },
  { key: 'cpa', header: 'Cost/conv.', width: 110, align: 'right',
    sort: (r) => r.cost_per_conversion,
    render: (r) => r.conversions > 0 && r.cost_per_conversion > 0
      ? fmtMoney(r.cost_per_conversion)
      : <span className="text-muted-foreground">—</span> },
  { key: 'cvr', header: 'CVR', width: 90, align: 'right',
    sort: (r) => r.cvr ?? -1,
    render: (r) => r.cvr != null
      ? `${(r.cvr * 100).toFixed(1)}%`
      : <span className="text-muted-foreground">—</span> },
  { key: 'quality_score', header: 'Quality Score', width: 130, align: 'right',
    sort: (r) => r.quality_score ?? -1,
    render: (r) => r.quality_score ?? <span className="text-muted-foreground">—</span> },
  { key: 'expected_ctr', header: 'Expected CTR', width: 130,
    sort: (r) => qualityRank(r.expected_ctr), render: (r) => qualityLabel(r.expected_ctr) },
  { key: 'landing_page_experience', header: 'Landing Page Exp.', width: 150,
    sort: (r) => qualityRank(r.landing_page_experience),
    render: (r) => qualityLabel(r.landing_page_experience) },
  { key: 'ad_relevance', header: 'Ad Relevance', width: 130,
    sort: (r) => qualityRank(r.ad_relevance), render: (r) => qualityLabel(r.ad_relevance) },
];

export function KeywordReviewTab({ tenantId: tenantIdProp }: KeywordReviewTabProps) {
  const storeTenant = useTenantStore((s) => s.currentTenant);
  const tenantId = (tenantIdProp === undefined ? storeTenant : tenantIdProp) ?? null;
  const queryClient = useQueryClient();
  const structure = useManagementStructure(tenantId ?? '');

  const keywordStatus = useMutation({
    mutationFn: ({ names, status }: { names: string[]; status: 'ENABLED' | 'PAUSED' }) =>
      setKeywordStatus(tenantId ?? '', names, status),
    onSuccess: (res) => {
      // ⚠ Report what CHANGED, never what was requested.
      if (res.failed?.length) {
        toast.warning(res.message, {
          description: `${res.failed.length} did not change.`,
        });
      } else {
        toast.success(res.message);
      }
      queryClient.invalidateQueries({ queryKey: ['ga-structure'] });
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: unknown } } })
        ?.response?.data?.detail;
      const flag =
        typeof detail === 'object' && detail !== null
          ? (detail as { flag?: string }).flag
          : undefined;
      toast.error(
        flag ? `This action is switched off (${flag}).` : 'Could not change keyword status.',
      );
    },
  });

  if (!tenantId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No workspace selected</CardTitle>
          <CardDescription>
            Pick a workspace to see its keywords.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Keywords</CardTitle>
          <CardDescription>
            Search keywords with match type and status, ranked by spend.
            Negative keywords are excluded, because they are not keywords you
            bid on. Rows that spent without converting are marked in red.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ReadGate state={structure.data} isLoading={structure.isLoading}>
            <ManagementTable<GaKeywordRow>
              rows={structure.data?.keywords ?? []}
              columns={keywordColumns}
              getRowId={(r) => r.resource_name}
              searchFields={(r) => [r.text, r.ad_group_name, r.campaign_name, r.match_type]}
              filters={[
                { key: 'match', label: 'Match type', value: (r) => r.match_type },
                { key: 'status', label: 'Status', value: (r) => r.status },
              ]}
              // ⛔ NO ACTIONS WHEN THE SERVER WOULD REFUSE THEM. These buttons
              // used to render with the flag off and return 503 on click, and
              // the operator test doc had to explain that away as expected.
              // `keyword_write` comes from the module owning the write gate,
              // so the button and the refusal cannot disagree.
              //
              // Passing none also removes the selection checkboxes:
              // ManagementTable derives `hasBulk` from this array, and
              // selection here exists only to feed these two actions.
              bulkActions={
                canWrite(structure.data?.capabilities, 'keyword_write')
                  ? [
                      {
                        label: 'Pause selected',
                        onRun: (rows) =>
                          keywordStatus.mutate({
                            names: rows.map((r) => r.resource_name),
                            status: 'PAUSED',
                          }),
                        enabledFor: (rows) => rows.some((r) => r.status === 'ENABLED'),
                      },
                      {
                        label: 'Enable selected',
                        onRun: (rows) =>
                          keywordStatus.mutate({
                            names: rows.map((r) => r.resource_name),
                            status: 'ENABLED',
                          }),
                        enabledFor: (rows) => rows.some((r) => r.status !== 'ENABLED'),
                      },
                    ]
                  : []
              }
              defaultSortKey="cost"
              defaultSortDir="desc"
              searchPlaceholder="Search keywords…"
              emptyText="No Search keywords on this account in this window."
              rowTone={(r) => (r.is_waste ? 'bad' : '')}
            />
          </ReadGate>
        </CardContent>
      </Card>
    </div>
  );
}

export default KeywordReviewTab;
